import {
  BookSize,
  BookTextStyle,
  type DesignedCoverId,
} from '@mixtiles/web-backend-shared'
import { z } from 'zod'
import { DefaultTextStyle } from '../../components/PhotoBook/CoverFontSelector/coverFonts'
import { type Cover } from '../../components/PhotoBook/PhotoBookProvider/PhotoBook.types'
import {
  type CoverStyleId,
  isCoverStyleId,
} from '../../features/photoBookAiCovers/coverStyles.consts'
import { LEGACY_PHOTO_BOOK_ID, PHOTO_BOOK_ID_LENGTH } from './photoBookId'

/**
 * The photo book as it sits in localStorage: the editor's own shape, persisted
 * verbatim. This is the only place a stored book is normalized, so every shape
 * a blob can carry is mapped here — a generated cover keeps its image under
 * `coverUrl` or `aiGeneratedCoverUrl`, a cover with no photo either carries
 * `photo: null` or omits the key, and render state (`pageSide`, `isFrontOpen`)
 * is dropped by naming every field the editor actually reads.
 *
 * Blobs go through `JSON.stringify`, so an unset field is absent rather than
 * `null` — except for a value that came from the backend, which `photoBookDTO`
 * copies onto the photo as-is when it restores an account book. `dateTaken`,
 * `caption.style` and `caption.fontHeight` reach storage as null that way, and
 * the cover's caption — the object itself, or the style inside it — is missing
 * outright from some stored books, so all of those read as what the DTO path
 * reads them as. Everything else is `.optional()`, and null marks the slots a
 * writer deliberately empties — the cover photo and a page's photo slots.
 * Nothing else falls back, so a blob that fails here is reported rather than
 * coerced past, and any flexibility this schema ends up needing is added later
 * against a named field in the report.
 */

export const CropSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  cropType: z.string().optional(),
  wasSmartCropped: z.boolean().optional(),
})

const CaptionSchema = z.object({
  text: z.string(),
  // A book whose stored style is missing keeps every page and loses only its
  // font, which is how the DTO path reads the same value.
  style: z
    .nativeEnum(BookTextStyle)
    .nullish()
    .transform((style) => style ?? DefaultTextStyle),
  fontHeight: z
    .number()
    .nullish()
    .transform((fontHeight) => fontHeight ?? undefined),
})

const PagePhotoSchema = z.object({
  uid: z.string(),
  dateTaken: z
    .number()
    .nullish()
    .transform((dateTaken) => dateTaken ?? undefined),
  gps: z.object({ latitude: z.number(), longitude: z.number() }).optional(),
  cropParams: z.record(CropSchema),
  hasLowResWarn: z.boolean().optional(),
  lowQualityApproved: z.boolean().optional(),
  fixedIndex: z.boolean().optional(),
  appendedToEnd: z.boolean().optional(),
  happyData: z.object({ score: z.number(), faces: z.number() }).optional(),
  caption: CaptionSchema.optional(),
  doFacesOverflow: z.boolean().optional(),
})

// Older blobs modelled a photo slot as nullable, mirroring the backend DTO. An
// empty slot carries nothing, so it is dropped rather than failing the book.
const PageSideContentSchema = z.object({
  photos: z
    .array(PagePhotoSchema.nullable())
    .transform((photos) => photos.flatMap((photo) => (photo ? [photo] : []))),
})

const PageSchema = z.object({
  front: PageSideContentSchema,
  back: PageSideContentSchema,
})

// Not validated against the catalog's id union: an id retired from the catalog
// still has to round-trip, since rejecting the book over one would cost the
// user everything else in it. The type is the union's because the parsed cover
// is a `DesignedCover`.
const DesignedCoverIdSchema = z.custom<DesignedCoverId>(
  (value) => typeof value === 'string'
)

const CoverStyleIdSchema = z.custom<CoverStyleId>(isCoverStyleId)

const AiGeneratedCoverSchema = z.object({
  type: z.literal('ai-generated'),
  coverUrl: z.string(),
  backgroundColor: z.string().optional(),
  textColor: z.string().optional(),
  aiGeneratedTitle: z.string().optional(),
  style: CoverStyleIdSchema.optional(),
  caption: CaptionSchema,
})

const CoverSchema = z
  .object({
    caption: CaptionSchema.default({ text: '', style: DefaultTextStyle }),
    // A cover with no photo either carries the key as null or omits it.
    photo: PagePhotoSchema.nullish(),
    designedCoverId: DesignedCoverIdSchema.optional(),
    // The live cover keeps its generated image under `coverUrl`; a stored one
    // may keep it under `aiGeneratedCoverUrl` instead.
    coverUrl: z.string().optional(),
    aiGeneratedCoverUrl: z.string().optional(),
    backgroundColor: z.string().optional(),
    textColor: z.string().optional(),
    aiGeneratedTitle: z.string().optional(),
    style: CoverStyleIdSchema.optional(),
    // A generated cover stored before it kept the book's title has none to
    // give back, so a photo or designed cover replacing it starts untitled.
    bookCaption: CaptionSchema.default({ text: '', style: DefaultTextStyle }),
  })
  // The stored `type` discriminant is ignored in favour of what the cover
  // actually carries, in the same precedence the editor used before it existed.
  .transform((cover, ctx): Cover => {
    const generatedCoverUrl = cover.coverUrl ?? cover.aiGeneratedCoverUrl
    if (generatedCoverUrl) {
      return {
        type: 'ai-generated' as const,
        coverUrl: generatedCoverUrl,
        backgroundColor: cover.backgroundColor,
        textColor: cover.textColor,
        aiGeneratedTitle: cover.aiGeneratedTitle,
        style: cover.style,
        caption: cover.caption,
        bookCaption: cover.bookCaption,
      }
    }

    if (cover.designedCoverId) {
      return {
        type: 'designed' as const,
        designedCoverId: cover.designedCoverId,
        caption: cover.caption,
      }
    }

    if (cover.photo) {
      return {
        type: 'photo' as const,
        photo: cover.photo,
        caption: cover.caption,
      }
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'cover has no photo, designed cover or generated image',
    })
    return z.NEVER
  })

export const StoredPhotoBookSchema = z
  .object({
    // A book stored before books had an identity was the browser's only book,
    // and reads back under the id every id-less payload gets — the same one its
    // account copy comes back under, so the two merge instead of stacking.
    bookId: z.string().min(PHOTO_BOOK_ID_LENGTH).default(LEGACY_PHOTO_BOOK_ID),
    // A book stored before this field has no edit time to report, and sorts oldest
    // against every book that does.
    lastEditedAt: z.number().default(0),
    sortKey: z.number().optional(),
    cover: CoverSchema,
    pages: z.array(PageSchema),
    size: z.nativeEnum(BookSize),
    // Both predate the writers that fill them, so absent is legitimate and
    // defaults in.
    quantity: z.number().default(1),
    aiGeneratedCovers: z.array(AiGeneratedCoverSchema).default([]),
  })
  // A book stored before it had a sort key takes its edit time as one: zero for
  // every such book would leave them tied, with no midpoint to insert between.
  .transform((book) => ({
    ...book,
    sortKey: book.sortKey ?? book.lastEditedAt,
  }))
